import React, { useState, useEffect, useRef, useCallback } from 'react';
import logo from '../../Logo/logo.png';
import { InteractiveButton } from '../common/InteractiveButton';

export default function LandingNavbar({ company, onNavigate, view }) {
    const [navScrolled, setNavScrolled]     = useState(false);
    const [navVisible, setNavVisible]       = useState(true);
    const lastScrollY    = useRef(0);
    const lastTapTime    = useRef(0);
    const ticking        = useRef(false);

    /* ── scroll hide/show (rAF-throttled, passive) ── */
    useEffect(() => {
        if (view === 'login') {
            setNavScrolled(true);
            setNavVisible(true);
            return;
        }

        const onScroll = () => {
            if (ticking.current) return;
            ticking.current = true;
            requestAnimationFrame(() => {
                const y = window.scrollY;
                setNavScrolled(y > 20);

                if (y < 60) {
                    setNavVisible(true);
                } else if (y > lastScrollY.current + 4 && window.innerWidth < 768) {
                    // scrolling down — hide after a small dead-band (MOBILE ONLY)
                    setNavVisible(false);
                } else if (y < lastScrollY.current - 4) {
                    // scrolling up — show
                    setNavVisible(true);
                }
                lastScrollY.current = y;
                ticking.current = false;
            });
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [view]);

    /* ── smooth section scroll ── */
    const handleScroll = useCallback((id) => {
        if (view !== 'home') {
            onNavigate('home');
            setTimeout(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 120);
        } else {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [view, onNavigate]);

    /* ── logo click: single = home, double = scroll to top ── */
    const handleLogoClick = useCallback(() => {
        const now = Date.now();
        const DOUBLE_TAP_MS = 320;

        if (now - lastTapTime.current < DOUBLE_TAP_MS) {
            lastTapTime.current = 0;
            onNavigate('home');
            requestAnimationFrame(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        } else {
            lastTapTime.current = now;
            onNavigate('home');
        }
    }, [onNavigate]);

    return (
        <nav className={`hp-nav ${navScrolled ? 'hp-nav--scrolled' : ''} ${!navVisible ? 'hp-nav--hidden' : ''}`}>
            <div className="hp-nav-inner">
                <div className="hp-logo">
                    <img
                        src={logo}
                        alt="BharatQA"
                        className="hp-logo-img hp-logo-img--interactive"
                        style={{ cursor: 'pointer', height: '38px', width: 'auto', display: 'block', margin: 0 }}
                        onClick={handleLogoClick}
                        draggable={false}
                    />
                </div>

                {view !== 'changelog' && (
                  <div className="hp-nav-links">
                      <button onClick={() => handleScroll('sample-reports')}>Sample Report</button>
                      <button onClick={() => handleScroll('technical-details')}>Technical</button>
                      <button onClick={() => handleScroll('features')}>Features</button>
                      <button onClick={() => handleScroll('pricing')}>Pricing</button>
                      <button onClick={() => handleScroll('testers')}>Testers</button>
                  </div>
                )}

                <div className="hp-nav-right">
                    {!company && view === 'login' && (
                        <InteractiveButton
                            variant="outline"
                            onClick={() => onNavigate('home')}
                            style={{ padding: '8px 20px', fontSize: '14px' }}
                        >
                            Home
                        </InteractiveButton>
                    )}
                    {!company && view !== 'login' && (
                        <InteractiveButton
                            variant="primary"
                            onClick={() => onNavigate('login')}
                            style={{ padding: '8px 20px', fontSize: '14px' }}
                        >
                            Sign In
                        </InteractiveButton>
                    )}
                    {company && (
                        <InteractiveButton
                            variant="primary"
                            className="hp-nav-dash"
                            onClick={() => onNavigate('dashboard')}
                            style={{ padding: '7px 16px', fontSize: '13px' }}
                        >
                            Dashboard →
                        </InteractiveButton>
                    )}
                </div>
            </div>
        </nav>
    );
}
